<script setup lang="ts">
import { Menu, X, ChevronDown, Phone, Search } from "lucide-vue-next";
import MegaMenu from "./header/MegaMenu.vue";
import MobileMenu from "./header/MobileMenu.vue";
import SearchDialog from "./header/SearchDialog.vue";
import IntentCaptureModal from "./header/IntentCaptureModal.vue";

/**
 * Main site header with:
 * - Logo
 * - Desktop mega menu navigation (6 dropdowns)
 * - Search trigger (opens dialog)
 * - Phone number
 * - CTA button
 * - Mobile hamburger menu
 *
 * Sticky on scroll with subtle shadow
 */

const navigation = useNavigation();
const route = useRoute();
const { open: openQuoteModal } = useIntentModal();
const { trackCtaClick, trackPhoneClick } = useAnalytics();

// UI State
const isMobileMenuOpen = ref(false);
const isSearchOpen = ref(false);
const activeMenuId = ref<string | null>(null);

// Hover intent delays to prevent flickering
let hoverTimeout: ReturnType<typeof setTimeout> | null = null;
let leaveTimeout: ReturnType<typeof setTimeout> | null = null;

function openMenu(menuId: string) {
  if (leaveTimeout) {
    clearTimeout(leaveTimeout);
    leaveTimeout = null;
  }
  hoverTimeout = setTimeout(() => {
    activeMenuId.value = menuId;
  }, 100);
}

function closeMenu() {
  if (hoverTimeout) {
    clearTimeout(hoverTimeout);
    hoverTimeout = null;
  }
  leaveTimeout = setTimeout(() => {
    activeMenuId.value = null;
  }, 150);
}

function closeMenuImmediate() {
  if (hoverTimeout) clearTimeout(hoverTimeout);
  if (leaveTimeout) clearTimeout(leaveTimeout);
  activeMenuId.value = null;
}

function toggleMobileMenu() {
  isMobileMenuOpen.value = !isMobileMenuOpen.value;
}

function openSearch() {
  isSearchOpen.value = true;
}

// Close mobile menu on route change
watch(
  () => route.fullPath,
  () => {
    isMobileMenuOpen.value = false;
    closeMenuImmediate();
  }
);

// Lock body scroll when mobile menu is open
watch(isMobileMenuOpen, (isOpen) => {
  if (isOpen) {
    document.body.style.overflow = "hidden";
  } else {
    document.body.style.overflow = "";
  }
});

// Handle escape key for mega menu
function handleKeydown(e: KeyboardEvent) {
  if (e.key === "Escape") {
    closeMenuImmediate();
  }
}

onMounted(() => {
  document.addEventListener("keydown", handleKeydown);
});

onUnmounted(() => {
  document.removeEventListener("keydown", handleKeydown);
  if (hoverTimeout) clearTimeout(hoverTimeout);
  if (leaveTimeout) clearTimeout(leaveTimeout);
});
</script>

<template>
  <header class="sticky top-0 z-50 bg-dark shadow-lg">
    <div class="container-page">
      <div class="flex h-21 items-center justify-between gap-4">
        <!-- Logo -->
        <NuxtLink to="/" class="shrink-0" aria-label="Metwide Communications - Home">
          <img
            src="/logo-white.svg"
            alt="Metwide Communications"
            class="h-10 w-auto sm:h-12"
          />
        </NuxtLink>

        <!-- Desktop Navigation -->
        <nav
          class="hidden lg:flex lg:items-center lg:gap-0.5"
          aria-label="Main navigation"
        >
          <div
            v-for="item in navigation.megaMenuItems"
            :key="item.id"
            class="relative"
            @mouseenter="openMenu(item.id)"
            @mouseleave="closeMenu"
          >
            <button
              type="button"
              class="flex items-center gap-1 rounded-lg px-2.5 py-2 text-[13px] font-medium uppercase tracking-wide text-white/90 transition-colors hover:bg-white/10 hover:text-white xl:px-3"
              :class="{ 'bg-white/10 text-white': activeMenuId === item.id }"
              :aria-expanded="activeMenuId === item.id"
              aria-haspopup="true"
              @click="activeMenuId = activeMenuId === item.id ? null : item.id"
            >
              {{ item.label }}
              <ChevronDown
                class="h-4 w-4 transition-transform duration-200"
                :class="{ 'rotate-180': activeMenuId === item.id }"
                aria-hidden="true"
              />
            </button>
          </div>
        </nav>

        <!-- Right side: Search, Phone, CTA -->
        <div class="flex items-center gap-2 sm:gap-3">
          <!-- Search button -->
          <button
            type="button"
            class="hidden items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium uppercase text-white/90 transition-colors hover:bg-white/10 hover:text-white sm:flex"
            @click="openSearch"
          >
            <Search class="h-4 w-4" aria-hidden="true" />
            <span class="hidden xl:inline">Search</span>
          </button>

          <!-- Phone CTA (desktop) - Accent/Orange button -->
          <a
            :href="navigation.phone.href"
            class="hidden items-center gap-1.5 rounded-full border border-white/30 px-4 py-2.5 text-sm font-semibold text-white transition-all hover:border-white/60 hover:bg-white/10 lg:inline-flex"
            @click="trackPhoneClick('header')"
          >
            <Phone class="h-4 w-4" aria-hidden="true" />
            <span>{{ navigation.phone.label }}</span>
          </a>

          <!-- Quote CTA Button - Primary action with Amber -->
          <button
            type="button"
            class="hidden rounded-full bg-mw-accent-500 px-5 py-2.5 text-sm font-bold text-slate-900 shadow-sm transition-all hover:bg-mw-accent-400 hover:shadow-md sm:inline-flex"
            @click="trackCtaClick(navigation.cta.label, 'header', '/contact'); openQuoteModal('free-quote')"
          >
            {{ navigation.cta.label }}
          </button>

          <!-- Mobile: Search icon -->
          <button
            type="button"
            class="rounded-lg p-2 text-white/90 hover:bg-white/10 hover:text-white sm:hidden"
            aria-label="Search"
            @click="openSearch"
          >
            <Search class="h-5 w-5" />
          </button>

          <!-- Mobile: Hamburger menu -->
          <button
            type="button"
            class="rounded-lg p-2 text-white/90 hover:bg-white/10 hover:text-white lg:hidden"
            :aria-expanded="isMobileMenuOpen"
            aria-controls="mobile-menu"
            aria-label="Toggle menu"
            @click="toggleMobileMenu"
          >
            <Menu v-if="!isMobileMenuOpen" class="h-6 w-6" />
            <X v-else class="h-6 w-6" />
          </button>
        </div>
      </div>
    </div>

    <!-- Desktop Mega Menu Dropdowns -->
    <MegaMenu
      v-for="item in navigation.megaMenuItems"
      :key="item.id"
      :item="item"
      :is-open="activeMenuId === item.id"
      @close="closeMenuImmediate"
      @mouse-enter="openMenu(item.id)"
      @mouse-leave="closeMenu"
    />

    <!-- Mobile Menu -->
    <MobileMenu
      id="mobile-menu"
      :is-open="isMobileMenuOpen"
      :navigation="navigation"
      @close="isMobileMenuOpen = false"
      @open-search="openSearch"
    />

    <!-- Search Dialog -->
    <SearchDialog v-model:open="isSearchOpen" />

    <!-- Intent Capture Modal (for "I need to..." links) -->
    <IntentCaptureModal />
  </header>
</template>
